import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/components/utils/utils';
import { useI18n } from '@/hooks/useI18n';
import { EmailTemplatePayload } from '@/lib/admin/email-templates/models/email-template.model';
import { AvailableVariablesPanel } from '@/lib/admin/email-templates/components/AvailableVariablesPanel';
import { TemplateChannelCard } from '@/lib/admin/email-templates/components/TemplateChannelCard';
import { VariablesBar } from '@/lib/admin/email-templates/components/VariablesBar';
import { useVariableInsertion } from '@/lib/admin/email-templates/hooks/useVariableInsertion';

export type EmailTemplateModalMode = 'create' | 'edit';

interface EmailTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: EmailTemplateModalMode;
  /** Valori iniziali: record esistente (edit), copia (duplica) o record vuoto (create). */
  initialValues: EmailTemplatePayload;
  /** Chiavi già esistenti, per la validazione di unicità lato client. */
  existingKeys: string[];
  onSubmit: (payload: EmailTemplatePayload) => Promise<void>;
  isSaving: boolean;
}

const TEMPLATE_KEY_PATTERN = /^[a-z0-9_]+$/;

/**
 * Scheda di un template: un record, i suoi due canali affiancati.
 *
 * Si apre già in modifica: aprire la riga è di per sé l'intenzione di cambiare
 * qualcosa, e un passaggio in più prima di poter scrivere era solo attrito. Il
 * salvataggio è uno solo per tutta la scheda: a DB il record è uno, quindi un
 * Salva = un UPDATE.
 *
 * Chiave e descrizione sono scrivibili solo in creazione: la prima è la chiave
 * primaria, la seconda descrive *quando parte* il messaggio ed è il contratto con
 * il trigger che lo invia, non un campo di redazione.
 */
export const EmailTemplateModal = ({
  isOpen,
  onClose,
  mode,
  initialValues,
  existingKeys,
  onSubmit,
  isSaving,
}: EmailTemplateModalProps) => {
  const { t } = useI18n();
  const { registerTarget, insertAtCursor } = useVariableInsertion();

  const isCreate = mode === 'create';

  const [values, setValues] = useState<EmailTemplatePayload>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  // La modale è montata di continuo dal parent: il reset dei valori avviene
  // all'apertura (e a ogni cambio di record), non allo smontaggio.
  useEffect(() => {
    if (!isOpen) return;
    setValues(initialValues);
    setErrors({});
  }, [isOpen, initialValues]);

  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(initialValues),
    [values, initialValues]
  );

  const setField = <K extends keyof EmailTemplatePayload>(
    field: K,
    value: EmailTemplatePayload[K]
  ) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => {
      if (!previous[field as string]) return previous;
      const next = { ...previous };
      delete next[field as string];
      return next;
    });
  };

  /** Campi testo nullable: la stringa vuota va salvata come NULL, non come ''. */
  const setNullableField = (
    field: 'description' | 'mailgun_draft_link' | 'redirect_url' | 'notification_message',
    value: string
  ) => setField(field, value.length > 0 ? value : null);

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    const key = values.template_key.trim();

    if (!key) {
      nextErrors.template_key = t('emailTemplates.errors.keyRequired');
    } else if (!TEMPLATE_KEY_PATTERN.test(key)) {
      nextErrors.template_key = t('emailTemplates.errors.keyFormat');
    } else if (isCreate && existingKeys.includes(key)) {
      nextErrors.template_key = t('emailTemplates.errors.duplicateKey');
    }

    if (!values.subject.trim()) {
      nextErrors.subject = t('emailTemplates.errors.subjectRequired');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    await onSubmit({ ...values, template_key: values.template_key.trim() });
  };

  const handleRequestClose = () => {
    if (isSaving) return;
    if (isDirty) {
      setConfirmCloseOpen(true);
      return;
    }
    onClose();
  };

  const discardChanges = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleRequestClose}
        size="xl"
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span>
              {isCreate
                ? t('emailTemplates.modal.createTitle')
                : t('emailTemplates.modal.detailTitle')}
            </span>
            {!isCreate && (
              <span className="font-mono text-sm text-gray-500">{initialValues.template_key}</span>
            )}
            {isDirty && (
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
                {t('emailTemplates.modal.unsavedChanges')}
              </Badge>
            )}
          </span>
        }
        footer={
          <>
            <Button variant="outline" onClick={handleRequestClose} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving || (!isDirty && !isCreate)}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4 p-5">
          {/* Chiave: editabile solo in creazione (è la chiave primaria del record). */}
          {isCreate && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="template_key">{t('emailTemplates.fields.templateKey')}</Label>
              <Input
                id="template_key"
                value={values.template_key}
                onChange={(event) => setField('template_key', event.target.value)}
                placeholder={t('emailTemplates.fields.templateKeyPlaceholder')}
                className={cn('font-mono', errors.template_key && 'border-red-500')}
              />
              <p className={cn('text-xs', errors.template_key ? 'text-red-600' : 'text-gray-500')}>
                {errors.template_key ?? t('emailTemplates.fields.templateKeyHint')}
              </p>
            </div>
          )}

          {/* Descrizione: prima riga, sempre in sola lettura fuori dalla creazione. */}
          {isCreate ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="description">{t('emailTemplates.fields.description')}</Label>
              <Input
                id="description"
                value={values.description ?? ''}
                onChange={(event) => setNullableField('description', event.target.value)}
                placeholder={t('emailTemplates.fields.descriptionPlaceholder')}
              />
              <p className="text-xs text-gray-500">{t('emailTemplates.fields.descriptionHint')}</p>
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 border-l-[3px] border-l-gray-300 bg-gray-50 px-4 py-3">
              <p className="text-sm text-gray-700">
                {values.description ?? t('emailTemplates.table.noDescription')}
              </p>
            </div>
          )}

          <VariablesBar variables={values.available_variables} onInsert={insertAtCursor} />

          {/* Le variabili si aggiungono solo alla nascita del template. */}
          {isCreate && (
            <AvailableVariablesPanel
              variables={values.available_variables}
              onChange={(next) => setField('available_variables', next)}
              onInsert={insertAtCursor}
            />
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <TemplateChannelCard
              channel="email"
              values={values}
              error={errors.subject}
              onFieldChange={setField}
              onNullableChange={setNullableField}
              registerTarget={registerTarget}
            />
            <TemplateChannelCard
              channel="notification"
              values={values}
              onFieldChange={setField}
              onNullableChange={setNullableField}
              registerTarget={registerTarget}
            />
          </div>
        </div>
      </Modal>

      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        {/* Sopra la Modal (portale a z-index ≥ 60): overlay e contenuto vanno alzati. */}
        <AlertDialogContent className="z-[130]" overlayClassName="z-[125]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('emailTemplates.modal.discardTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('emailTemplates.modal.discardDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={discardChanges}>
              {t('emailTemplates.modal.discardConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
